import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import ContrastIcon from '@mui/icons-material/Contrast';
import { useAccessibility, FontSize } from '../contexts/AccessibilityContext';

const AccessibilityToolbar: React.FC = () => {
  const { t } = useTranslation();
  const { fontSize, highContrast, setFontSize, setHighContrast, fontSizeMultiplier } = useAccessibility();

  const handleFontSizeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSize: FontSize | null
  ) => {
    if (newSize !== null) {
      setFontSize(newSize);
    }
  };

  const handleContrastToggle = () => {
    setHighContrast(!highContrast);
  };

  // Base size for touch targets (48px minimum for accessibility)
  const buttonSize = Math.max(48, 40 * fontSizeMultiplier);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Font Size Toggle */}
      <ToggleButtonGroup
        value={fontSize}
        exclusive
        onChange={handleFontSizeChange}
        aria-label={t('accessibility.fontSize', 'Font Size')}
        size="small"
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          '& .MuiToggleButton-root': {
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            minWidth: buttonSize,
            minHeight: buttonSize,
            fontSize: `${0.9 * fontSizeMultiplier}rem`,
            fontWeight: 600,
            '&.Mui-selected': {
              bgcolor: 'rgba(255, 255, 255, 0.25)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.35)',
              },
            },
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.15)',
            },
          },
        }}
      >
        <ToggleButton value="normal" aria-label={t('accessibility.fontSize.normal', 'Normal')}>
          <Tooltip title={t('accessibility.fontSize.normal', 'Normal')}>
            <span>A</span>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="large" aria-label={t('accessibility.fontSize.large', 'Large')}>
          <Tooltip title={t('accessibility.fontSize.large', 'Large')}>
            <span style={{ fontSize: '1.15em' }}>A</span>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="xlarge" aria-label={t('accessibility.fontSize.xlarge', 'Extra Large')}>
          <Tooltip title={t('accessibility.fontSize.xlarge', 'Extra Large')}>
            <span style={{ fontSize: '1.3em' }}>A</span>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* High Contrast Toggle */}
      <Tooltip
        title={
          highContrast
            ? t('accessibility.highContrast.off', 'Disable High Contrast')
            : t('accessibility.highContrast.on', 'Enable High Contrast')
        }
      >
        <IconButton
          onClick={handleContrastToggle}
          aria-label={t('accessibility.highContrast', 'High Contrast')}
          aria-pressed={highContrast}
          sx={{
            color: 'white',
            bgcolor: highContrast ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
            minWidth: buttonSize,
            minHeight: buttonSize,
            '&:hover': {
              bgcolor: highContrast
                ? 'rgba(255, 255, 255, 0.35)'
                : 'rgba(255, 255, 255, 0.15)',
            },
          }}
        >
          <ContrastIcon sx={{ fontSize: `${1.5 * fontSizeMultiplier}rem` }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default AccessibilityToolbar;
